import React, { useEffect, useState } from "react"
import InputSelect from "../InputSelect"
import {
	Option,
	renderOptionsProps,
	renderToggleProps as renderInputProps,
} from "../interfaces"
import {
	ESCAPE_KEY,
	KEYUP,
	MOUSEDOWN,
	renderDefaultOption,
	renderInputDefault,
} from "../utils"
import { concatClassNames } from "helpers/jsxHelpers"
import "./index.scss"
import withVisibility from "components/Shared/Hocs/withVisibility/withVisibility"

export type SelectFinalProps = {
	/**
	 * close menu when reselect option
	 */
	canReselectOption?: boolean
	className?: string
	closeOnSelect?: boolean
	disabled?: boolean
	footerMenu?: React.ReactNode
	icon?: React.ReactNode
	id: string
	label?: string
	value?: string
	options?: Option[]
	selectFirstOptionDefault?: boolean
	size?: "small" | "medium" | "large"
	placeholder?: string
	onChange?: (option: Option) => void
	onChangeInputLabel?: (option: Option) => void
	onClick?: () => void
	onInit?: (option: Option) => void
	onClickOutSide?: () => void
	renderOptions?: (props: renderOptionsProps) => JSX.Element
	renderInput?: (props: renderInputProps) => JSX.Element
}

export const EMPTY_OPTION = { code: "", name: "" }

const Select = (props: SelectFinalProps) => {
	const {
		closeOnSelect = true,
		disabled = false,
		id,
		icon,
		label,
		options = [],
		selectFirstOptionDefault = true,
		value,
		size = "medium",
		footerMenu,
		canReselectOption = true,
		className,
		placeholder,
		onChange = () => undefined,
		onClick = () => undefined,
		onInit = () => undefined,
		onChangeInputLabel = () => undefined,
		onClickOutSide = () => undefined,
		renderOptions = renderDefaultOption,
		renderInput = renderInputDefault,
	} = props

	const parseValue = (options: Option[], value?: string) => {
		const option = options.find((option) => option.code === value)

		return option ?? options[0]
	}

	const getValue = (options: Option[], value?: string) =>
		selectFirstOptionDefault ? parseValue(options, value) : undefined

	const [optionSelected, setOptionSelected] = useState<Option | undefined>(() =>
		getValue(options, value)
	)
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

	const handleOnPressKey = (event: KeyboardEvent) => {
		event.key === ESCAPE_KEY && setIsMenuOpen(false)
	}

	const handleClickOutside = (event: any) => {
		if (event.target.closest("div.dropdown")?.id !== `dropdown-${id}`) {
			onClickOutSide()
			setIsMenuOpen(false)
		}
	}

	const handleOpenOptions = () => {
		onClick()
		!disabled && setIsMenuOpen(!isMenuOpen)
	}

	const isOptionSelected = (option: Option) =>
		optionSelected?.code === option.code

	const canReslectOption = (option: Option) => {
		if (!isOptionSelected(option)) {
			return true
		}

		return canReselectOption
	}

	const selectOption = (newOption: Option) => {
		if (optionSelected?.code !== newOption.code) {
			onChange(newOption)
			setOptionSelected(newOption)
		}
	}

	const updateLabelInput = (option: Option) => {
		if (optionSelected?.name !== option.name) {
			onChangeInputLabel(option)
			setOptionSelected(option)
		}
	}

	const handleOptionSelected = (option: Option) => {
		if (!canReslectOption(option)) {
			return
		}

		selectOption(option)
		closeOnSelect && closeMenu()
	}

	const closeMenu = () => {
		setIsMenuOpen(false)
	}

	const createOptions = () =>
		options.map((option, index) => {
			const optionItem = renderOptions({
				option,
				index,
				optionSelected,
			})

			return React.cloneElement(optionItem, {
				key: option.code,
				onClick: () => {
					handleOptionSelected(option)
				},
				role: "option",
			})
		})

	const createInput = () => {
		const toggle = renderInput({
			optionSelected,
			disabled,
			isMenuOpen,
			placeholder,
		})

		return React.cloneElement(toggle, {
			onClick: () => {
				handleOpenOptions()
			},
		})
	}

	const addClassDisabled = () => (disabled ? "dropdown-disabled" : undefined)

	const addClassActive = () =>
		isMenuOpen && !disabled ? "dropdown-active" : undefined

	useEffect(() => {
		document.addEventListener(KEYUP, handleOnPressKey)
		optionSelected && onInit(optionSelected)

		return () => {
			document.removeEventListener(KEYUP, handleOnPressKey)
		}
	}, [])

	useEffect(() => {
		document.addEventListener(MOUSEDOWN, handleClickOutside)

		return () => {
			document.removeEventListener(MOUSEDOWN, handleClickOutside)
		}
	})

	useEffect(() => {
		if (options.length === 0) {
			return
		}

		const option = options.find((option) => option.code === value)
		option && selectOption(option)
	}, [value, options])

	useEffect(() => {
		if (options.length === 0) {
			selectOption(EMPTY_OPTION)
			updateLabelInput(EMPTY_OPTION)
			return
		}

		const option = options.find(
			(option) => option.code === optionSelected?.code
		)
		option && updateLabelInput(option)
	}, [options])

	return (
		<div
			id={`dropdown-${id}`}
			className={concatClassNames([
				"dropdown",
				addClassDisabled(),
				addClassActive(),
				className,
				size,
			])}
			role="combobox"
		>
			{label && (
				<label className="dropdown-label" htmlFor={`dropdown-${id}`}>
					<span className="dropdown-label-text">{label}</span>
				</label>
			)}
			<div
				aria-labelledby={`dropdown-${id}`}
				className="dropdown-toggle"
				aria-disabled={disabled}
				onClick={handleOpenOptions}
				data-testid={`dropdown-${id}`}
			>
				<InputSelect size={size} isOpen={isMenuOpen} icon={icon}>
					{createInput()}
				</InputSelect>
			</div>
			{isMenuOpen && (
				<div className="dropdown-menu" role="list-box">
					{createOptions()}
					{footerMenu}
				</div>
			)}
		</div>
	)
}
export default withVisibility(Select)
